import {
  AUTH_STORAGE_KEYS,
  clearAuthStorage,
  getApiUrl,
} from "@/lib/auth-utils";
import { getOrCreateDeviceId } from "@/lib/device-id";
import { ApiResponse } from "@/types/api-response";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import JSONbigFactory from "json-bigint";

// Create JSON parser for BigInt handling
const JSONbig = JSONbigFactory({ storeAsString: true });

// Create base API instance
export const axiosInstance: AxiosInstance = axios.create({
  baseURL: getApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 100000,
  transformRequest: [
    function (data, headers) {
      // Convert BigInt to string for JSON transport
      if (
        data &&
        headers &&
        String(
          headers["Content-Type"] || headers["content-type"] || ""
        ).includes("application/json")
      ) {
        return JSON.stringify(data, (_k, v) =>
          typeof v === "bigint" ? v.toString() : v
        );
      }
      return data;
    },
  ],
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use((config) => {
  const isForm =
    typeof FormData !== "undefined" && config.data instanceof FormData;
  if (isForm && config.headers) {
    // remove any preset content-type so the browser can set boundary
    delete (config.headers as any)["Content-Type"];
    delete (config.headers as any)["content-type"];
  }
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["DeviceID"] = getOrCreateDeviceId();
  return config;
});

// Response interceptor for token refresh and error handling
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestPath = originalRequest?.url?.split(/[?#]/)[0].replace(/\/+$/, '');

    // Credential failures belong to the sign-in form, not session recovery.
    if (!originalRequest || requestPath?.endsWith('/auth/login') || requestPath?.endsWith('/auth/refresh')) {
      return Promise.reject(error);
    }

    // Failed login returns 401 — do not treat as session expiry / redirect.
    if (originalRequest?.url?.includes("/auth/login")) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(
          AUTH_STORAGE_KEYS.REFRESH_TOKEN
        );
        if (refreshToken) {
          const response = await axios.post(getApiUrl("/auth/refresh"), {
            refreshToken: refreshToken,
          });

          // Extract tokens from Spring Boot response format
          const { accessToken, refreshToken: newRefreshToken } =
            response.data.data;

          localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
          localStorage.setItem(
            AUTH_STORAGE_KEYS.REFRESH_TOKEN,
            newRefreshToken
          );

          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.log(refreshError, 'refresh error');
        // Refresh token failed, logout user
        clearAuthStorage();
        if (window.location.pathname !== '/login') window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden - user doesn't have permission
    if (error.response?.status === 403) {
      clearAuthStorage();
      if (window.location.pathname !== '/login') window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);
// Helper functions for error handling
function createApiError<T>(
  response: ApiResponse<T>,
  fallbackMessage: string
): Error {
  const error = new Error(response.message || fallbackMessage);
  (error as any).status = response.status;
  (error as any).statusText = response.statusText;
  (error as any).response = response;
  return error;
}

function createErrorResponse<T>(
  status: number,
  statusText: string,
  message: string
): ApiResponse<T> {
  return {
    data: null as T,
    success: false,
    status,
    statusText,
    message,
    errors: [message],
  };
}

export async function apiRequest<T>(
  method: "get" | "post" | "put" | "delete" | "patch",
  url: string,
  options: AxiosRequestConfig = {},
  onUploadProgress?: (evt: any) => void,
  signal?: AbortSignal
): Promise<ApiResponse<T>> {
  try {
    const cfg: AxiosRequestConfig = {
      method,
      url,
      ...options,
      signal: signal ?? options.signal,
      onUploadProgress: onUploadProgress ?? options.onUploadProgress,
    };
    const axiosResponse: AxiosResponse = await axiosInstance(cfg);
    const status = axiosResponse.status;

    // Handle 204 No Content (and 204-206 range for successful no-content responses)
    if (status >= 204 && status < 207) {
      return {
        data: null as T,
        success: true,
        status,
        statusText: axiosResponse.statusText || "Deleted successfully.",
        message: "Operation completed successfully",
      };
    }

    // Ensure data is parsed (transformResponse should handle this, but double-check)
    let responseData = axiosResponse.data;
    if (typeof responseData === "string") {
      try {
        responseData = JSONbig.parse(responseData);
      } catch {
        // If parsing fails, treat as empty response
        responseData = null;
      }
    }

    const response = responseData as ApiResponse<T>;

    // Handle case where response.data might be empty/undefined
    if (!response || typeof response !== "object") {
      // If we have a successful status but no response body, create a success response
      if (status >= 200 && status < 300) {
        return {
          data: null as T,
          success: true,
          status,
          statusText: axiosResponse.statusText || "OK",
          message: "Operation completed successfully",
        };
      }
    }

    // Throw error if response indicates failure
    if (!response.success) {
      throw createApiError(response, "Request failed");
    }

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status ?? 500;

      // Handle canceled requests (don't throw)
      if (axiosError.code === "ERR_CANCELED") {
        return createErrorResponse<T>(
          499,
          "Client Closed Request",
          "Upload canceled"
        );
      }

      // Parse error response data if it's a string
      let errorResponseData = axiosError.response?.data;
      if (typeof errorResponseData === "string") {
        try {
          errorResponseData = JSONbig.parse(errorResponseData);
        } catch {
          errorResponseData = null;
        }
      }
      const errorResponse = errorResponseData as ApiResponse<T> | undefined;

      // Throw errors for 500+ status codes
      if (status >= 500 && status < 600) {
        const apiError =
          errorResponse ??
          createErrorResponse<T>(
            status,
            axiosError.response?.statusText ?? "Error",
            axiosError.message
          );
        throw createApiError(apiError, "Server error");
      }

      // Return error response for other status codes (4xx, etc.)
      return (
        errorResponse ??
        createErrorResponse<T>(
          status,
          axiosError.response?.statusText ?? "Error",
          axiosError.message
        )
      );
    }

    // Throw unknown errors
    throw createApiError(
      createErrorResponse<T>(
        500,
        "Unknown Error",
        error instanceof Error ? error.message : "An unknown error occurred"
      ),
      "An unknown error occurred"
    );
  }
}

export const apiClient = {
  get: <T, P extends object | string | number | boolean | void>(
    url: string,
    params?: P
  ) => apiRequest<T>("get", url, { params }),
  post: <T, D extends object | string | number | boolean>(
    url: string,
    data?: D
  ) => apiRequest<T>("post", url, { data }),
  put: <T, D extends object | string | number | boolean>(
    url: string,
    data?: D
  ) => apiRequest<T>("put", url, { data }),
  patch: <T, D extends object | string | number | boolean>(
    url: string,
    data?: D
  ) => apiRequest<T>("patch", url, { data }),
  delete: <T>(url: string) => apiRequest<T>("delete", url),
  postForm: <T>(url: string, form: FormData, options?: AxiosRequestConfig) =>
    apiRequest<T>("post", url, { data: form, ...(options || {}) }),
};

export const uploadForm = <T>(
  url: string,
  form: FormData,
  opts?: {
    config?: AxiosRequestConfig;
    onProgress?: (evt: ProgressEvent) => void;
    signal?: AbortSignal;
  }
) =>
  apiRequest<T>(
    "post",
    url,
    { data: form, ...(opts?.config || {}) },
    opts?.onProgress,
    opts?.signal
  );

// poster function bypasses apiRequest to handle blob responses directly
// This is intentional for cases where we need raw response (e.g., file downloads)
export const poster = async (
  args: string | [string, AxiosRequestConfig],
  body: any,
  headers?: any
) => {
  const [url, config] = Array.isArray(args) ? args : [args, {}];
  const res = await axiosInstance.post(url, body, {
    ...config,
    headers,
    // When responseType is 'blob', don't try to parse the response
    transformResponse:
      config.responseType === "blob" ? (data) => data : undefined,
  });
  return res;
};
